<template>
	<div class="welcome-popup-overlay">
		<div class="welcome-popup" @click.stop>
			<div class="welcome-header">
				<h2 class="welcome-title">Welcome to PartnerUp!</h2>
				<h3 class="welcome-subtitle" v-html="subtitle || 'Let\'s find your first collab'" />
			</div>

			<div class="welcome-steps">
				<div class="step-item">
					<div class="step-icon-wrapper">
						<Icon type="matches" class="svg" colour="#ffffff" />
						<span class="step-number">1</span>
					</div>
					<div class="step-content">
						<h4 class="step-title">Meet your Matches</h4>
						<p class="step-description">Check out who you've been matched with 🫶 find a brand you like and send them a Request.</p>
					</div>
				</div>

				<div class="step-item">
					<div class="step-icon-wrapper">
						<Icon type="pitch" class="svg" colour="#ffffff" />
						<span class="step-number">2</span>
					</div>
					<div class="step-content">
						<h4 class="step-title">Join opportunities in the Opportunities Area</h4>
						<p class="step-description">Short on time or lean teams? You can piggyback 🐷 on these ready-to-join collaborations.</p>
					</div>
				</div>

				<div class="step-item">
					<div class="step-icon-wrapper">
						<Icon type="bulb" class="svg" colour="#ffffff" />
						<span class="step-number">3</span>
					</div>
					<div class="step-content">
						<h4 class="step-title">Have your ideal brands come to you</h4>
						<p class="step-description">In the Opportunites Area you can feature your business and have your ideal brands come to you.</p>
					</div>
				</div>
			</div>

			<div class="welcome-actions">
				<button @click="handleGetStarted" class="button get-started-btn">Get Started</button>
			</div>
			<p class="help-text">
				Need more help using PartnerUp? <br />Click the <span class="help-text-link" @click="handleHelp">Help</span> button in the top right
				corner. Or check out our <span class="help-text-link" @click="handleHowToVideos">How-to Videos</span> in the menu bar
			</p>
		</div>
	</div>
</template>

<script>
import Icon from "../Display/BaseIcon.vue";
import { getCurrentInstance } from "vue";
import ApiService from "./../../services/api.service";
export default {
	name: "WelcomePopup",
	components: {
		Icon,
	},
	props: {
		type: {
			type: String,
			required: true,
		},
		subtitle: {
			type: String,
			default: "",
		},
	},
	emits: ["close", "showHelp"],
	setup(props, { emit }) {
		const vm = getCurrentInstance().proxy;
		const store = vm.$store;

		const updateVar = (payload) => store.commit("general/updateVar", payload);

		const handleClose = () => {
			ApiService.post(`users/action/welcome`);
			emit("close");
		};

		const handleGetStarted = () => {
			handleClose();
		};

		const handleOverlayClick = () => {
			handleClose();
		};

		const handleStepClick = (step) => {
			if (step === 1) vm.$router.push("/matches");
			if (step === 3) vm.$router.push("/inspiration");
			handleClose();
		};

		const handleHelp = () => {
			emit("showHelp");
			handleClose();
		};

		const handleHowToVideos = () => {
			updateVar({ varName: "showVideos", info: true });
			handleClose();
		};

		return {
			handleClose,
			handleHelp,
			handleHowToVideos,
			handleGetStarted,
			handleOverlayClick,
			handleStepClick,
		};
	},
};
</script>

<style scoped lang="less">
@import "./../../assets/css/less/_settings.less";

.welcome-popup-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: @overlayScrimStrong;
	z-index: 99999999;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 20px;
}

.welcome-popup {
	position: relative;
	padding: 30px 40px;
	text-align: center;
	max-width: 700px;
	width: 100%;
	background: white;
	border-radius: @radiusXl;
	.elevation.modalWorkspace();
	max-height: 90vh;
	max-height: 90dvh;
	overflow-y: auto;

	.help-text {
		font-size: 14px;
		color: @textGrey;
		line-height: 1.6;
		margin: 10px 0 0;

		.help-text-link {
			color: @formDefaultColour;
			text-decoration: underline;
			cursor: pointer;
		}
	}

	.welcome-header {
		margin-bottom: 25px;

		.welcome-title {
			font-size: 32px;
			font-weight: 700;
			color: @darkContrast;
			margin: 0;
		}

		.welcome-subtitle {
			font-size: 24px;
			font-weight: 600;
			color: @formDefaultColour;
			margin: 0;
		}

		.welcome-description {
			font-size: 16px;
			color: @textGrey;
			line-height: 1.6;
			margin: 0;
		}
	}

	.welcome-steps {
		margin-bottom: 25px;

		.step-item {
			display: flex;
			align-items: flex-start;
			text-align: left;
			margin-bottom: 20px;
			padding: 20px;
			background: @bgGrey;
			border-radius: @radiusLg;
			transition: all 0.3s ease;

			&:hover {
				.step-icon-wrapper .svg {
					transform: scale(1.1) rotate(360deg);
				}
			}

			&:last-child {
				margin-bottom: 0;
			}

			.step-icon-wrapper {
				position: relative;
				flex-shrink: 0;
				width: 60px;
				height: 60px;
				margin-right: 24px;
				background: @formDefaultColour;
				border-radius: @radiusCircle;
				display: flex;
				align-items: center;
				justify-content: center;

				.svg {
					width: 50%;
					height: 50%;
					transition: transform 0.8s ease;
				}

				.step-icon {
					font-size: 24px;
					color: white;
				}

				.step-number {
					position: absolute;
					top: -8px;
					right: -8px;
					width: 24px;
					height: 24px;
					background: @darkContrast;
					color: white;
					border-radius: @radiusCircle;
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 14px;
					font-weight: 700;
				}
			}

			.step-content {
				flex: 1;

				.step-title {
					font-size: 18px;
					font-weight: 600;
					color: @darkContrast;
					margin: 0 0 4px 0;
				}

				.step-description {
					font-size: 14px;
					color: @textGrey;
					line-height: 1.5;
					margin: 0;
				}
			}
		}
	}

	.welcome-actions {
		.get-started-btn {
			font-size: 18px;
			padding: 16px 48px;
			background: @formDefaultColour;
			color: white;
			border: none;
			margin: 0;

			&:hover {
				background: darken(@formDefaultColour, 10%);
			}
		}
	}
}

@media (max-width: 768px) {
	.welcome-popup {
		padding: 24px;

		.welcome-header {
			margin-bottom: 30px;

			.welcome-title {
				font-size: 24px;
			}

			.welcome-description {
				font-size: 14px;
			}
		}

		.welcome-steps {
			.step-item {
				padding: 16px;

				.step-icon-wrapper {
					width: 48px;
					height: 48px;
					margin-right: 16px;

					.step-icon {
						font-size: 20px;
					}

					.step-number {
						width: 20px;
						height: 20px;
						font-size: 12px;
					}
				}

				.step-content {
					.step-title {
						font-size: 16px;
					}

					.step-description {
						font-size: 13px;
					}
				}
			}
		}

		.welcome-actions {
			.get-started-btn {
				font-size: 16px;
				padding: 14px 36px;
			}
		}
	}
}
</style>